/*
 * Copyright (c) 2022 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { memo, useContext } from 'react';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Avatar, Grid, IconButton, Typography } from '@mui/material';
import Icon from '@mui/material/Icon';
import NameValueGroup from '../../Common/NameValueGroup';
import { TableTooltip } from '../../../StyledComponents/TableTooltip';
import { createDetailsGroupData, createGeneralGroupData } from './DocumentsListConfig';
import { PAYMENT_ICON_BY_PAYMENT_TYPE } from '../utils';
import { WithTranslation } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { DocumentsListTableData } from './DocumentsListConfig';
import { theme } from '../../../../assets/style/them';
import { PaymentTypesAsStrings } from '../../../../data/api/documents/documentsClient.typings';
import { Link } from 'react-router5';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import { RouteId, RouteParam } from '../../../../router/routes';
import clsx from 'clsx';
import { DATA_UNAVAILABLE } from '../../../../utils/utils';
import { AppAbilityContext } from '../../../../accessibility/ability';

const useStyles = makeStyles((_theme: Theme) =>
    createStyles({
        row: {
            borderBottomColor: theme.palette.disabledText.light,
            borderBottom: '1px solid',
        },
        titleCell: {
            padding: theme.spacing(2, 0, 0, 4),
        },
        titleCellWithLink: {
            [theme.breakpoints.down('lg')]: {
                display: 'flex',
                alignItems: 'center',
                paddingTop: theme.spacing(1),
            },
        },
        title: {
            fontSize: 14,
            fontWeight: 700,
            marginRight: theme.spacing(1),
        },
        dataCell: {
            padding: theme.spacing(0, 2, 0, 8),
            width: '100%',
        },
        titleWrap: {
            display: 'flex',
            flexDirection: 'row',
        },
        restoredAvatar: {
            width: 18,
            height: 18,
            color: theme.palette.teal.main,
            background: theme.palette.teal.light,
            marginRight: theme.spacing(0.5),
        },
        restoredIcon: {
            fontSize: 14,
        },
    }),
);

export const paymentTypesAsIcons = (values: string[], t: WithTranslation['t']) => {
    return (
        <div style={{ display: 'flex', flexDirection: 'row', marginBottom: theme.spacing(0.5) }}>
            {values.map((value, index) => {
                return PAYMENT_ICON_BY_PAYMENT_TYPE[value as PaymentTypesAsStrings] !== undefined ? (
                    <TableTooltip
                        key={index}
                        title={t(`document.paymentTypesList.${value.toLowerCase()}`)}
                        placement="top"
                    >
                        <Avatar
                            style={{
                                width: 30,
                                height: 30,
                                marginRight: theme.spacing(1),
                                color: theme.palette.teal.main,
                                background: theme.palette.teal.light,
                            }}
                        >
                            <Icon> {PAYMENT_ICON_BY_PAYMENT_TYPE[value as PaymentTypesAsStrings]} </Icon>
                        </Avatar>
                    </TableTooltip>
                ) : (
                    <Typography> {value} </Typography>
                );
            })}
        </div>
    );
};

interface DocumentCardViewItemProps {
    data: DocumentsListTableData;
    createdAtUtc: string;
}

const DocumentCardViewItem = (props: DocumentCardViewItemProps) => {
    const classes = useStyles();
    const { data, createdAtUtc } = props;
    const { t } = useTranslation();
    const ability = useContext(AppAbilityContext);
    const idToShowDocumentCardAsPicture = ability.can('viewScreen', 'DOCUMENT_CARD_PICT_SCREEN');

    return (
        <Grid container className={classes.row}>
            <Grid
                item
                md={12}
                lg={2}
                className={clsx(classes.titleCell, idToShowDocumentCardAsPicture ? classes.titleCellWithLink : null)}
            >
                <div className={classes.titleWrap}>
                    {data.restored && ability.can('access', 'RESTORED_DOCUMENT') && (
                        <TableTooltip placement="top" title={t('document.restoredDocument')}>
                            <Avatar className={classes.restoredAvatar}>
                                <Icon className={classes.restoredIcon}>{'memory'}</Icon>
                            </Avatar>
                        </TableTooltip>
                    )}
                    <Typography
                        className={classes.title}
                        style={{ color: data.title === DATA_UNAVAILABLE ? theme.palette.error.main : 'unset' }}
                    >
                        {data.title}
                    </Typography>
                </div>
                {idToShowDocumentCardAsPicture && (
                    <Link
                        routeName={RouteId.RegistryDocumentCardPict}
                        routeParams={{
                            [RouteParam.CashboxTaxNumber]: data.cashBoxTaxNumber,
                            [RouteParam.CreatedAtUtc]: createdAtUtc ? createdAtUtc : DATA_UNAVAILABLE,
                            [RouteParam.DocumentIdHash]: data.documentIdHash,
                        }}
                    >
                        <TableTooltip title={t('RegistryScreens.viewPictureDocument')} placement="top">
                            <IconButton size="large" color="primary">
                                <ReceiptLongRoundedIcon />
                            </IconButton>
                        </TableTooltip>
                    </Link>
                )}
            </Grid>
            <Grid item md={12} lg={5} className={classes.dataCell}>
                <NameValueGroup valueAlign="left" data={createGeneralGroupData(data, t, ability)} />
            </Grid>
            <Grid item md={12} lg={5} className={classes.dataCell}>
                <NameValueGroup valueAlign="left" data={createDetailsGroupData(data, t)} />
            </Grid>
        </Grid>
    );
};

export default memo(DocumentCardViewItem);
